import { useEffect, useState } from 'react';

/**
 * The trial this account would actually be granted, in one place.
 *
 * Four buttons are labelled from it - the three pricing cards, the seat wall on
 * the staff page, and the plan the setup guide suggests - and they have to
 * agree with each other AND with StripeController, which applies the same test
 * before putting subscription_data.trial_period_days on the checkout session.
 * A second copy of this rule is how a button ends up offering a free week that
 * nobody receives.
 *
 * Two things must both hold:
 *
 *   trial_days     the `plans` row grants a trial at all
 *   trial_eligible THIS account still qualifies - false once they have ever had
 *                  a Stripe subscription, so cancelling cannot buy another one
 *
 * Either missing means no trial, and the button says "Subscribe" - which is the
 * truthful label whenever we cannot show otherwise.
 */

let cache = null;
let inflight = null;
let explained = false;

/**
 * Says once, in the console, why no trial is being offered.
 *
 * There are three reasons a button reads "Subscribe" and they are
 * indistinguishable from the outside - an API that has not been redeployed, a
 * plans row with trial_days 0, and an account that has already had its trial.
 * All three are correct behaviour, which is exactly what makes the silence
 * expensive: the only way to tell them apart was to go and read the database.
 */
function explainOnce(plans, offer) {
  if (explained) return;
  explained = true;

  const maxDays = Math.max(0, ...Object.values(offer.days));

  if (!('trial_eligible' in plans)) {
    console.info(
      '[trial] /db_plans did not return trial_eligible, so no trial is offered. '
      + 'The API host is running a build older than the one that added it.',
    );
    return;
  }

  if (maxDays === 0) {
    console.info(
      '[trial] every plan row has trial_days 0, so no trial is offered. '
      + 'Set trial_days on the plans rows (ids 1, 2, 4) to change that.',
    );
    return;
  }

  if (!offer.eligible) {
    console.info(
      '[trial] the plans grant a trial but this account is not eligible - it has '
      + 'had a Stripe subscription before. Buttons read "Subscribe".',
    );
  }
}

/**
 * Cached for the life of the page. /db_plans is already fetched on load by the
 * staff page, and four labels should not cost four requests. A checkout that
 * succeeds comes back through a full page load, so the cache cannot outlive the
 * state it describes.
 */
export function loadTrialOffer(axiosClient) {
  if (cache) return Promise.resolve(cache);
  if (inflight) return inflight;

  inflight = axiosClient
    .get('/db_plans')
    .then(({ data }) => {
      const plans = data?.data || {};

      cache = {
        eligible: Boolean(plans.trial_eligible),
        days: {
          standard: Number(plans.standard?.trial_days) || 0,
          premium: Number(plans.premium?.trial_days) || 0,
          enterprise: Number(plans.enterprise?.trial_days) || 0,
        },
      };

      explainOnce(plans, cache);

      return cache;
    })
    .catch(() => {
      // Deliberately NOT cached. A failed lookup is not an answer, so the next
      // screen is free to ask again; until one succeeds every button reads
      // "Subscribe".
      return { eligible: false, days: {} };
    })
    .finally(() => {
      inflight = null;
    });

  return inflight;
}

/**
 * Seeds the cache from a /db_plans response the caller already has, so a screen
 * that fetches it anyway does not pay for a second request.
 */
export function seedTrialOffer(dbPlansData) {
  if (!dbPlansData) return;

  cache = {
    eligible: Boolean(dbPlansData.trial_eligible),
    days: {
      standard: Number(dbPlansData.standard?.trial_days) || 0,
      premium: Number(dbPlansData.premium?.trial_days) || 0,
      enterprise: Number(dbPlansData.enterprise?.trial_days) || 0,
    },
  };
}

/**
 * @returns {{ eligible: boolean, trialDays: (planKey: string) => number }}
 */
export function useTrialOffer(axiosClient) {
  const [offer, setOffer] = useState(() => cache || { eligible: false, days: {} });

  useEffect(() => {
    let cancelled = false;

    loadTrialOffer(axiosClient).then((value) => {
      if (!cancelled) setOffer(value);
    });

    return () => {
      cancelled = true;
    };
    // axiosClient is a new object on every render - useAxiosClient calls
    // axios.create() each time - so listing it here would refetch forever.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    eligible: offer.eligible,
    trialDays: (planKey) =>
      (offer.eligible ? Number(offer.days?.[planKey]) || 0 : 0),
  };
}

export default useTrialOffer;
